import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useAppStore } from "@/store/useAppStore";
import { isConfiguredStatus } from "@/hooks/useBackendConnections";
import { TEMP_COMING_SOON_CONNECTION_KINDS } from "@/lib/connectionStatus";
import { pairingUserMessage } from "@/lib/pairingErrors";
import { newPairingClientSessionId } from "@/lib/pairingSession";
import { rowsByKindMap } from "@/lib/connectionRows";
import {
  runConnectionChannelAction,
  disconnectConnectionChannel,
  connectionActionTitle,
} from "@/pages/settings/connectionChannelActions";
import {
  CONNECTION_KINDS,
  applyOauthRedirectFromUrl,
  loadGoogleOauthStatus,
  seedMissingConnections,
} from "@/pages/settings/connectionsBootstrap";

export function useConnectionsSettings() {
  const { auth } = useAppStore();
  // Workspace team role (owner/admin/member/viewer) — not multi-agent "agent" logins.
  const workspaceRole = auth?.server?.role || "owner";
  const canEdit = workspaceRole === "owner" || workspaceRole === "admin";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [pairingConn, setPairingConn] = useState(null);
  const [pairingPayload, setPairingPayload] = useState(null);
  const [pairingBusy, setPairingBusy] = useState(false);
  const [telegramOpen, setTelegramOpen] = useState(false);
  const [quoraOpen, setQuoraOpen] = useState(false);
  const [linkedinOpen, setLinkedinOpen] = useState(false);
  const [whatsappManageOpen, setWhatsappManageOpen] = useState(false);
  const [whatsappManageRow, setWhatsappManageRow] = useState(null);
  const [whatsappManageBusy, setWhatsappManageBusy] = useState(false);
  const [disconnectBusy, setDisconnectBusy] = useState(null);
  const [telegramSyncBusy, setTelegramSyncBusy] = useState(false);
  const [googleOauthStatus, setGoogleOauthStatus] = useState(null);

  const fetchRows = useCallback(async () => {
    const { connectionsApi } = await import("@/lib/api");
    const r = await connectionsApi.list();
    setRows(r.connections || []);
    return r.connections || [];
  }, []);

  useEffect(() => {
    let cancelled = false;
    loadGoogleOauthStatus()
      .then((s) => { if (!cancelled) setGoogleOauthStatus(s); })
      .catch(() => { if (!cancelled) setGoogleOauthStatus(null); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    applyOauthRedirectFromUrl(fetchRows);
  }, [fetchRows]);

  useEffect(() => {
    let cancelled = false;
    queueMicrotask(async () => {
      try {
        await seedMissingConnections(canEdit, fetchRows, setSeeding);
      } catch (e) {
        if (!cancelled) toast.error(e?.message || "Couldn't load connections");
      } finally {
        if (!cancelled) setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [canEdit, fetchRows]);

  useEffect(() => {
    const onVisibilityOrFocus = () => {
      if (document.visibilityState === "visible") {
        fetchRows().catch(() => {});
      }
    };
    window.addEventListener("focus", onVisibilityOrFocus);
    document.addEventListener("visibilitychange", onVisibilityOrFocus);
    return () => {
      window.removeEventListener("focus", onVisibilityOrFocus);
      document.removeEventListener("visibilitychange", onVisibilityOrFocus);
    };
  }, [fetchRows]);

  const byKind = rowsByKindMap(rows);
  // Exclude temporarily gated channels (Reddit/Quora) so live/pending stats
  // only reflect channels that can actually be paired right now.
  const countableKinds = useMemo(
    () => CONNECTION_KINDS.filter((c) => !TEMP_COMING_SOON_CONNECTION_KINDS.has(c.kind)),
    [],
  );
  const connectedCount = useMemo(() => countableKinds.filter((c) => {
    const row = byKind[c.kind];
    return row && isConfiguredStatus(
      row.status, row.configured, row.account_identifier, row.channel_config, row.kind,
    );
  }).length, [byKind, countableKinds]);
  const totalCount = countableKinds.length;

  const channelActionCtx = useMemo(() => ({
    canEdit,
    fetchRows,
    setQuoraOpen,
    setTelegramOpen,
    setLinkedinOpen,
    setTelegramSyncBusy,
    setPairingConn,
    setPairingPayload,
    setPairingBusy,
    setWhatsappManageOpen,
    setWhatsappManageRow,
  }), [canEdit, fetchRows]);

  const handleChannelAction = useCallback(
    (c, row) => runConnectionChannelAction(c, row, channelActionCtx),
    [channelActionCtx],
  );

  const handleDisconnect = useCallback(async (c, row) => {
    if (!canEdit || !row?.id) return;
    setDisconnectBusy(c.kind);
    try {
      await disconnectConnectionChannel(c, row, fetchRows);
    } catch (e) {
      toast.error(e?.message || `Couldn't disconnect ${c.label}`);
    } finally {
      setDisconnectBusy(null);
    }
  }, [canEdit, fetchRows]);

  const actionTitle = useCallback(
    (c, status) => connectionActionTitle(c, status, canEdit),
    [canEdit],
  );

  const closePairingModal = useCallback(() => {
    setPairingConn(null);
    setPairingPayload(null);
    // Refresh so expired/abandoned pairing rows flip back to Not connected.
    fetchRows().catch(() => {});
  }, [fetchRows]);

  const handlePairingRetry = useCallback(async () => {
    if (!pairingConn?.id) return;
    setPairingPayload(null);
    setPairingBusy(true);
    try {
      const { channelsApi } = await import("@/lib/api");
      const r = await channelsApi.pair(pairingConn.id, {
        client_session_id: newPairingClientSessionId(),
      });
      setPairingPayload(r);
    } catch (e) {
      toast.error(pairingUserMessage(e, "Couldn't restart pairing"));
      setPairingConn(null);
    } finally {
      setPairingBusy(false);
    }
  }, [pairingConn]);

  const handlePairingPaired = useCallback(async (deviceLabel) => {
    try { await fetchRows(); } catch { /* noop */ }
    let channelLabel;
    if (pairingConn?.kind === "whatsapp") channelLabel = "WhatsApp";
    else if (pairingConn?.kind === "telegram") channelLabel = "Telegram";
    else channelLabel = "Channel";
    const suffix = deviceLabel ? ` — ${deviceLabel}` : "";
    toast.success(`${channelLabel} connected${suffix}`);
    setTimeout(closePairingModal, 900);
  }, [pairingConn, fetchRows, closePairingModal]);

  const refreshAfterConfigure = useCallback(async () => {
    try { await fetchRows(); } catch { /* noop */ }
  }, [fetchRows]);

  const handleTelegramConfigured = useCallback(async (_r) => {
    await refreshAfterConfigure();
  }, [refreshAfterConfigure]);

  const handleTelegramReconfigure = useCallback((_c, row) => {
    if (!canEdit || !row?.id) return;
    setTelegramOpen(true);
  }, [canEdit]);

  const handleSimpleChannelConfigured = useCallback(async (message) => {
    await refreshAfterConfigure();
    toast.success(message);
  }, [refreshAfterConfigure]);

  const closeWhatsappManage = useCallback(() => {
    setWhatsappManageOpen(false);
    setWhatsappManageRow(null);
  }, []);

  const handleWhatsappPairDifferent = useCallback(async () => {
    const row = whatsappManageRow;
    const c = CONNECTION_KINDS.find((item) => item.kind === "whatsapp");
    if (!row?.id || !c) return;
    setWhatsappManageBusy(true);
    try {
      await disconnectConnectionChannel(c, row, fetchRows);
      closeWhatsappManage();
      const freshRows = await fetchRows();
      const freshRow = rowsByKindMap(freshRows).whatsapp;
      if (!freshRow?.id) {
        toast.error("WhatsApp connection row missing after disconnect — refresh and try again");
        return;
      }
      await runConnectionChannelAction(c, freshRow, {
        canEdit,
        fetchRows,
        setQuoraOpen,
        setTelegramOpen,
        setLinkedinOpen,
        setTelegramSyncBusy,
        setPairingConn,
        setPairingPayload,
        setPairingBusy,
        setWhatsappManageOpen,
        setWhatsappManageRow,
      });
    } catch (e) {
      toast.error(e?.message || "Couldn't start a fresh WhatsApp pairing");
    } finally {
      setWhatsappManageBusy(false);
    }
  }, [
    whatsappManageRow,
    canEdit,
    fetchRows,
    closeWhatsappManage,
  ]);

  const handleWhatsappManageDisconnect = useCallback(async () => {
    const row = whatsappManageRow;
    const c = CONNECTION_KINDS.find((item) => item.kind === "whatsapp");
    if (!row?.id || !c) return;
    setWhatsappManageBusy(true);
    try {
      await disconnectConnectionChannel(c, row, fetchRows);
      closeWhatsappManage();
    } catch (e) {
      toast.error(e?.message || "Couldn't disconnect WhatsApp");
    } finally {
      setWhatsappManageBusy(false);
    }
  }, [whatsappManageRow, fetchRows, closeWhatsappManage]);

  return {
    canEdit,
    loading,
    seeding,
    pairingConn,
    pairingPayload,
    pairingBusy,
    telegramOpen,
    setTelegramOpen,
    quoraOpen,
    setQuoraOpen,
    linkedinOpen,
    setLinkedinOpen,
    whatsappManageOpen,
    whatsappManageRow,
    whatsappManageBusy,
    closeWhatsappManage,
    handleWhatsappPairDifferent,
    handleWhatsappManageDisconnect,
    disconnectBusy,
    telegramSyncBusy,
    googleOauthStatus,
    byKind,
    connectedCount,
    totalCount,
    handleChannelAction,
    handleDisconnect,
    actionTitle,
    closePairingModal,
    handlePairingRetry,
    handlePairingPaired,
    handleTelegramConfigured,
    handleTelegramReconfigure,
    handleSimpleChannelConfigured,
    setPairingConn,
    setPairingPayload,
    setPairingBusy,
  };
}
